import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeFromDOMNode, $getNodeByKey } from 'lexical';
import { $isLayoutContainerNode } from '../nodes/LayoutNode';

const MIN_COL_WIDTH = 60;  // px — minimum column width before clamping
const GAP_ZONE      = 10;  // px — how close to the gap edge activates resize

// Parse "1fr 2fr 1fr" → [1, 2, 1]
function parseFr(template) {
  return template.trim().split(/\s+/).map(s => parseFloat(s) || 1);
}

// Round to 3 significant decimal places and build template string
function buildTemplate(frValues) {
  return frValues.map(v => `${Math.round(v * 1000) / 1000}fr`).join(' ');
}

export default function LayoutResizerPlugin() {
  const [editor] = useLexicalComposerContext();
  const stateRef = useRef(null);

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;

    // Orange indicator line — matches the editor's primary colour
    const line = document.createElement('div');
    line.style.cssText = [
      'position:fixed', 'pointer-events:none', 'z-index:99999',
      'background:#c97a2a', 'opacity:.75', 'display:none',
      'width:2px', 'top:0', 'height:100vh',
    ].join(';');
    document.body.appendChild(line);

    // ── Helpers ───────────────────────────────────────────────────────────────

    function findContainer(el) {
      while (el && el !== root) {
        if (el.dataset && 'lexicalLayoutContainer' in el.dataset) return el;
        el = el.parentElement;
      }
      return null;
    }

    function getItemsOf(containerEl) {
      return Array.from(
        containerEl.querySelectorAll(':scope > [data-lexical-layout-item]'),
      );
    }

    // Returns { items, leftIndex } when x falls in a column gap, otherwise null
    function detectGap(containerEl, x) {
      const items = getItemsOf(containerEl);
      for (let i = 0; i < items.length - 1; i++) {
        const lRect = items[i].getBoundingClientRect();
        const rRect = items[i + 1].getBoundingClientRect();
        if (x >= lRect.right - GAP_ZONE && x <= rRect.left + GAP_ZONE) {
          return { items, leftIndex: i };
        }
      }
      return null;
    }

    // ── Hover ─────────────────────────────────────────────────────────────────

    function onRootMove(e) {
      if (stateRef.current) return;
      const containerEl = findContainer(e.target);
      if (!containerEl) { root.style.cursor = ''; return; }
      root.style.cursor = detectGap(containerEl, e.clientX) ? 'col-resize' : '';
    }

    // ── Drag start ────────────────────────────────────────────────────────────

    function onRootDown(e) {
      if (stateRef.current) return;
      const containerEl = findContainer(e.target);
      if (!containerEl) return;
      const gap = detectGap(containerEl, e.clientX);
      if (!gap) return;

      e.preventDefault();
      e.stopPropagation();

      let containerKey   = null;
      let templateColumns = '';
      editor.read(() => {
        const node = $getNearestNodeFromDOMNode(containerEl);
        if ($isLayoutContainerNode(node)) {
          containerKey    = node.getKey();
          templateColumns = node.getTemplateColumns();
        }
      });
      if (!containerKey) return;

      const { items, leftIndex } = gap;
      const cRect      = containerEl.getBoundingClientRect();
      const gapPx      = parseFloat(
        window.getComputedStyle(containerEl).columnGap ||
        window.getComputedStyle(containerEl).gap       || '16',
      );
      const totalGap   = gapPx * (items.length - 1);
      const availWidth = cRect.width - totalGap;

      // Snapshot pixel widths of all columns now (before any drag movement)
      const itemWidths = items.map(item => item.getBoundingClientRect().width);

      stateRef.current = {
        startX: e.clientX,
        leftIndex,
        containerKey,
        templateColumns,
        itemWidths,
        availWidth,
        totalFr: parseFr(templateColumns).reduce((s, v) => s + v, 0),
      };

      line.style.cssText = [
        'position:fixed', 'pointer-events:none', 'z-index:99999',
        'background:#c97a2a', 'opacity:.75', 'display:block',
        'width:2px', 'top:0', 'height:100vh',
        `left:${e.clientX}px`,
      ].join(';');
    }

    // ── Move during drag ──────────────────────────────────────────────────────

    function onDocMove(e) {
      if (!stateRef.current) return;
      line.style.left = `${e.clientX}px`;
    }

    // ── Commit on release ─────────────────────────────────────────────────────

    function onDocUp(e) {
      const st = stateRef.current;
      if (!st) return;

      line.style.display = 'none';
      stateRef.current   = null;
      root.style.cursor  = '';

      const delta = e.clientX - st.startX;
      const { leftIndex, itemWidths, availWidth, totalFr, templateColumns } = st;

      // Clamp so neither neighbour shrinks below MIN_COL_WIDTH
      const maxDelta = itemWidths[leftIndex + 1] - MIN_COL_WIDTH;
      const minDelta = -(itemWidths[leftIndex] - MIN_COL_WIDTH);
      const clampedDelta = Math.max(minDelta, Math.min(maxDelta, delta));

      const newWidths = [...itemWidths];
      newWidths[leftIndex]     = itemWidths[leftIndex]     + clampedDelta;
      newWidths[leftIndex + 1] = itemWidths[leftIndex + 1] - clampedDelta;

      // Convert pixel widths back to fr units
      const newFrValues = parseFr(templateColumns).map((_, i) =>
        (newWidths[i] / availWidth) * totalFr,
      );

      const newTemplate = buildTemplate(newFrValues);

      editor.update(() => {
        const node = $getNodeByKey(st.containerKey);
        if ($isLayoutContainerNode(node)) {
          node.setTemplateColumns(newTemplate);
        }
      });
    }

    root.addEventListener('mousemove', onRootMove);
    root.addEventListener('mousedown', onRootDown);
    document.addEventListener('mousemove', onDocMove);
    document.addEventListener('mouseup',   onDocUp);

    return () => {
      root.removeEventListener('mousemove', onRootMove);
      root.removeEventListener('mousedown', onRootDown);
      document.removeEventListener('mousemove', onDocMove);
      document.removeEventListener('mouseup',   onDocUp);
      line.remove();
    };
  }, [editor]);

  return null;
}
